import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTrialPopupPageReady } from '../contexts/TrialPopupPageReadyContext';
import TrialPopup from './TrialPopup';

/**
 * SubscriptionChecker component
 *
 * Manages TrialPopup visibility:
 * - Flight details: show for non-premium on every visit only after FlightInfoPage has
 *   finished loading (flightInfoPageReady), so the popup does not hurt LCP/TBT.
 * - Home: show ONLY on initial load of the home page (user landed or reloaded on /).
 *   If user navigates from any other page to home, do NOT show (sticky: never show on in-app nav to home).
 * - Popup is shown after page ready (post-LCP) for better LCP.
 */
let initialPathname = undefined;
function getInitialPathname() {
  if (initialPathname === undefined && typeof window !== 'undefined') {
    initialPathname = window.location.pathname;
  }
  return initialPathname ?? '/';
}

let hasShownHomePopupInThisLoad = false;

const SubscriptionChecker = () => {
  const { hasActiveSubscription, loading } = useSubscription();
  const { flightInfoPageReady, isTrialPopupOpen, setIsTrialPopupOpen } = useTrialPopupPageReady();
  const [pageReady, setPageReady] = useState(false);
  const location = useLocation();

  const closeTrialPopup = () => {
    setIsTrialPopupOpen(false);
  };

  // Mark when page is fully loaded + main thread mostly idle (after LCP)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let done = false;
    const markReady = () => {
      if (done) return;
      done = true;
      setPageReady(true);
    };

    if (document.readyState === 'complete') {
      const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      ric(markReady, { timeout: 2000 });
    } else {
      const onLoad = () => {
        const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
        ric(markReady, { timeout: 2000 });
      };
      window.addEventListener('load', onLoad, { once: true });
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  useEffect(() => {
    if (loading || !pageReady) {
      setIsTrialPopupOpen(false);
      return;
    }

    if (hasActiveSubscription()) {
      setIsTrialPopupOpen(false);
      return;
    }

    const isFlightDetailsPage = location.pathname.startsWith('/flight-info');
    const isHomePage = location.pathname === '/';
    const landedOnHome = getInitialPathname() === '/';
    const queryParams = new URLSearchParams(location.search);
    const shouldOpenTrial = queryParams.get('openTrial') === 'true';

    if (shouldOpenTrial) {
      setIsTrialPopupOpen(true);
    } else if (isFlightDetailsPage) {
      // Only show trial popup when FlightInfoPage has finished loading (better LCP/TBT)
      if (flightInfoPageReady) {
        setIsTrialPopupOpen(true);
      } else {
        setIsTrialPopupOpen(false);
      }
    } else if (isHomePage) {
      // Show only when this document was opened/reloaded on home (initial load = home).
      // If user came from another page to home, do not show.
      if (landedOnHome && !hasShownHomePopupInThisLoad) {
        setIsTrialPopupOpen(true);
        hasShownHomePopupInThisLoad = true;
      } else {
        setIsTrialPopupOpen(false);
      }
    } else {
      setIsTrialPopupOpen(false);
    }
  }, [location.pathname, hasActiveSubscription, loading, pageReady, flightInfoPageReady]);

  return (
    <TrialPopup isOpen={isTrialPopupOpen} onClose={closeTrialPopup} />
  );
};

export default SubscriptionChecker;
